// IDwsHapticService's browser side: plays data-dws-haptic inside the user's tap and dwsHaptics.play from .NET.
// Core: the click listener has to be there before the first tap, and other modules play feedback synchronously
// inside their own gestures (drawer and notification swipes, touch drag).

import { publish } from "../shared/publish";

// IDwsHapticService: tactile feedback on touch devices. Components render data-dws-haptic="selection|light|medium|heavy|
// success|warning|error" on the element the user taps (only while the app has feedback switched on), and this one
// capturing click listener plays it in the browser, inside the user's gesture. That matters twice: a tap never waits
// for a server round trip, and iOS Safari only plays its tick inside a user gesture - a Blazor Server call arrives
// after the gesture has ended. .NET can still call dwsHaptics.play for outcomes only the server knows (a save that
// failed); Android plays those whenever they arrive.
//
// Android (Chromium, Firefox): navigator.vibrate(pattern). iOS/iPadOS Safari has no Vibration API; toggling an
// <input type="checkbox" switch> plays the system tick (iOS 18+), and a label click from script toggles it. Since iOS
// 18.4 that only works inside a user gesture (WebKit carries it through timers for about a second); iOS 26.5 ignores
// script clicks entirely, so there it stays silent. Everywhere else, and under prefers-reduced-motion, nothing happens.
//
// The hidden switch's clicks are stopped here, before any other listener sees them (this listener is on window, in the
// capture phase), so no outside-click handler (popover, menu, dialog) takes them for a click on the page. Stopping
// propagation does not cancel the label's activation, so the switch still toggles.
publish("dwsHaptics", {
    // vibrate: navigator.vibrate pattern in ms. ticks: start offsets in ms of the iOS switch ticks, which have one
    // fixed strength, so the kinds differ by their number of ticks only.
    patterns: {
        selection: { vibrate: 10, ticks: [0] },
        light: { vibrate: 15, ticks: [0] },
        medium: { vibrate: 25, ticks: [0] },
        heavy: { vibrate: 40, ticks: [0] },
        success: { vibrate: [15, 70, 25], ticks: [0, 100] },
        warning: { vibrate: [30, 90, 30], ticks: [0, 140] },
        error: { vibrate: [40, 60, 40, 60, 40], ticks: [0, 100, 200] }
    },

    _switchLabel: null,
    _synthetic: 0,

    reducedMotion: function () {
        return !!(window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
    },

    isIOS: function () {
        const ua = navigator.userAgent || "";
        // iPadOS reports itself as a Mac; the touch points give it away.
        return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
    },

    // "vibrate", "switch", "none" or "reduced-motion" - read by IDwsHapticService.GetSupportAsync.
    support: function () {
        const self = window.dwsHaptics;

        if (self.reducedMotion()) {
            return "reduced-motion";
        }

        if (typeof navigator.vibrate === "function") {
            return "vibrate";
        }

        return self.isIOS() ? "switch" : "none";
    },

    // Plays one feedback kind. Returns whether anything was started.
    play: function (kind: string): boolean {
        const self = window.dwsHaptics;
        const pattern = self.patterns[kind];

        if (!pattern) {
            return false;
        }

        const method = self.support();

        if (method === "vibrate") {
            try {
                return navigator.vibrate(pattern.vibrate) !== false;
            } catch (e) {
                return false;
            }
        }

        if (method !== "switch") {
            return false;
        }

        for (let i = 0; i < pattern.ticks.length; i++) {
            if (pattern.ticks[i] === 0) {
                self._tick();
            } else {
                setTimeout(self._tick, pattern.ticks[i]);
            }
        }

        return true;
    },

    _tick: function () {
        const self = window.dwsHaptics;
        const label = self._ensureSwitch();

        if (!label) {
            return;
        }

        self._synthetic++;

        try {
            label.click();
        } finally {
            self._synthetic--;
        }
    },

    // One hidden switch for the page, created on first use. display:none keeps it out of layout, the tab order and
    // the accessibility tree; the label still forwards its click.
    _ensureSwitch: function () {
        const self = window.dwsHaptics;

        if (self._switchLabel && self._switchLabel.isConnected) {
            return self._switchLabel;
        }

        if (!document.body) {
            return null;
        }

        const label = document.createElement("label");
        label.setAttribute("aria-hidden", "true");
        label.setAttribute("data-dws-haptic-switch", "");
        label.style.display = "none";

        const input = document.createElement("input");
        input.type = "checkbox";
        input.setAttribute("switch", "");
        input.tabIndex = -1;

        label.appendChild(input);
        document.body.appendChild(label);
        self._switchLabel = label;

        return label;
    },

    onClick: function (e) {
        const self = window.dwsHaptics;

        if (self._synthetic > 0) {
            e.stopImmediatePropagation();
            return;
        }

        const target = e.target;

        if (!(target instanceof Element)) {
            return;
        }

        const host = target.closest<HTMLButtonElement>("[data-dws-haptic]");

        // A disabled button gets no click at all; a DwsButton that is only aria-disabled (loading, busy form)
        // still does and must stay silent.
        if (!host || host.disabled || host.getAttribute("aria-disabled") === "true") {
            return;
        }

        self.play(host.getAttribute("data-dws-haptic"));
    }
});

window.addEventListener("click", window.dwsHaptics.onClick, true);
